import { useEffect } from 'react'
import { Card, Col, Divider, message, Row, Skeleton } from 'antd'
import { startCase } from 'lodash'

import CardSection, { SectionItem } from '~/components/shared/CardSection'
import HeadTitle from '~/components/HeadTitle'
import ModuleHeader from '~/components/ModuleHeader'
import { FrameType, useVehicleQuery } from '~/gql/generated/graphql'

type VehicleProps = {
  vehicleId: string
}

const missingValue = 'No data'

const formatValue = (value?: string | null) =>
  value ? startCase(value) : missingValue

const VehicleDebugView = ({ vehicleId }: VehicleProps) => {
  const { data, loading, error } = useVehicleQuery({
    variables: {
      vehicle_id: vehicleId,
    },
  })

  useEffect(() => {
    if (error) {
      message.error(error.message)
    }
  }, [error])

  const vehicle = data?.vehicle
  const moduleId = vehicle?.detail?.module?.id
  const title = vehicle?.frame_id
    ? `Vehicle ${vehicle.frame_id}`
    : `Vehicle ${vehicleId}`
  const cardTitle = vehicle ? (
    <ModuleHeader
      moduleId={moduleId ?? null}
      frameId={vehicle.frame_id}
      scheme={vehicle.detail?.scheme}
      frameType={vehicle.frame_type as FrameType}
      hardwareType={vehicle.hardware_type}
      vehicleId={vehicle.id}
      batteryPercent={null}
    />
  ) : (
    title
  )

  const vehicleItems: SectionItem[] = [
    { key: 'Vehicle ID', value: vehicle?.id ?? missingValue },
    { key: 'Frame ID', value: vehicle?.frame_id ?? missingValue },
    { key: 'Frame type', value: formatValue(vehicle?.frame_type) },
    { key: 'Hardware type', value: formatValue(vehicle?.hardware_type) },
    {
      key: 'Vehicle category',
      value: formatValue(vehicle?.vehicle_category),
    },
  ]

  const assignmentItems: SectionItem[] = [
    { key: 'Module ID', value: moduleId ?? missingValue },
    { key: 'Scheme', value: vehicle?.detail?.scheme?.name ?? missingValue },
    { key: 'Scheme ID', value: vehicle?.detail?.scheme?.id ?? missingValue },
  ]

  return (
    <>
      <HeadTitle pageTitle={title} />
      <Row gutter={16} className="fill-height">
        <Col xs={24} lg={10} className="flex-col">
          <Card data-testid="vehicle-debug-card" title={cardTitle}>
            {loading && <Skeleton active paragraph={{ rows: 6 }} />}

            {!loading && (
              <>
                <CardSection items={vehicleItems} />
                <Divider />
                <CardSection items={assignmentItems} />
              </>
            )}
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default VehicleDebugView
